import {Injectable} from '@angular/core';
import {Subject} from 'rxjs/internal/Subject';
import {NotificationModel} from '@models/notification.model';
import {HttpErrorResponse} from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  readonly WARNING_TIME: number = 10000;
  readonly SUCCESS_TIME: number = 5000;
  readonly ERROR_TIME: number = 18000;
  readonly INFO_TIME: number = 10000;

  private subject = new Subject<NotificationModel>();
  pool = this.subject.asObservable();

  constructor() {
  }

  /**
   * add warning alert on the page
   * @param message - alert message to display in alert content
   * @param timeout - timeout (milliseconds), time when user can see message
   */
  warning = (message: string,
             timeout: number = this.WARNING_TIME): void => {
    this.subject.next({
      message,
      time: timeout,
      type: 'alert alert-warning alert-dismissible fade show notification'
    });
  };

  /**
   * add success alert on the page
   * @param message - alert message to display in alert content
   * @param timeout - timeout (milliseconds), time when user can see message
   */
  success = (message: string,
             timeout = this.SUCCESS_TIME): void => {
    this.subject.next({
      message,
      time: timeout,
      type: 'alert alert-success alert-dismissible fade show notification'
    });
  };

  /**
   * add error alert on the page
   * @param error
   * @param timeout - timeout (milliseconds), time when user can see message
   */
  error = (error: string | HttpErrorResponse,
           timeout: number = this.ERROR_TIME): void => {
    this.subject.next({
      message: this.extractErrorMessage(error),
      time: timeout,
      type: 'alert alert-danger alert-dismissible fade show notification'
    });
  };

  extractErrorMessage(error, fallback?: string): string {
    let message = fallback || 'Something went wrong. Try again later';
    if (error instanceof HttpErrorResponse) {
      message = error?.error?.error?.message || message;
    } else if (typeof error === 'string') {
      message = error;
    }

    return message;
  }

  /**
   * File-download requests use responseType 'text'/'blob', so on failure the JSON error body
   * arrives undecoded (a raw string or Blob) instead of parsed - extractErrorMessage would read
   * `.error.message` off that raw body and always miss. Decode it first, then show it.
   */
  errorFromFileResponse = async (error: unknown, fallback?: string,
                                  timeout: number = this.ERROR_TIME): Promise<void> => {
    this.error(await this.decodeFileResponseError(error, fallback), timeout);
  };

  private async decodeFileResponseError(error: unknown, fallback?: string): Promise<string> {
    const defaultMessage = fallback || 'Something went wrong. Try again later';
    if (!(error instanceof HttpErrorResponse)) {
      return this.extractErrorMessage(error, fallback);
    }

    const body = error.error;
    if (!(body instanceof Blob) && typeof body !== 'string') {
      return this.extractErrorMessage(error, fallback);
    }

    try {
      const text = body instanceof Blob ? await body.text() : body;
      return JSON.parse(text)?.error?.message || defaultMessage;
    } catch {
      return defaultMessage;
    }
  }

  /**
   * A retryable error is a transient failure (no response reached the server, or the server/gateway
   * was momentarily unavailable) where reloading the same request has a real chance of succeeding.
   * Everything else (404, 410, 409, 400, 500, ...) is permanent for that request and reloading will
   * just reproduce the same error, so callers should not offer a retry for it.
   */
  private readonly RETRYABLE_HTTP_STATUSES = [0, 502, 503, 504];

  isRetryableError(error): boolean {
    return error instanceof HttpErrorResponse && this.RETRYABLE_HTTP_STATUSES.includes(error.status);
  }

  /**
   * add info alert on the page
   * @param message - alert message to display in alert content
   * @param timeout - timeout (milliseconds), time when user can see message
   */
  info = (message: string,
          timeout: number = this.INFO_TIME): void => {
    this.subject.next({
      message,
      time: timeout,
      type: 'alert alert-primary alert-dismissible fade show notification'
    });
  }
}
